import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PhoneCall, Search, Eye, FileText, Play, Download, Calendar,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import OutreachCallDetailModal from '@/components/admin/OutreachCallDetailModal';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import AdminRecordCard from '@/components/admin/AdminRecordCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const OUTCOME_STYLES = {
  interested: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  appointment_set: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  callback_requested: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  not_interested: 'bg-red-500/15 text-red-400 border-red-500/30',
  completed: 'bg-white/10 text-white/70 border-white/20',
};

function formatDate(value) {
  return formatDateTimeMdY(value, '—');
}

function OutcomeBadge({ outcome, label }) {
  const style = OUTCOME_STYLES[outcome] || OUTCOME_STYLES.completed;
  return (
    <span className={`inline-flex px-2 py-0.5 text-[11px] font-['Manrope'] border ${style}`}>
      {label || outcome}
    </span>
  );
}

export default function OutreachCallsPanel({ embedded = false }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailCall, setDetailCall] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchCalls = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/outreach/calls`, {
        headers,
        params: { limit: 200 },
      });
      setCalls(res.data.calls || []);
      setTotal(res.data.total ?? res.data.calls?.length ?? 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load outreach calls');
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const filteredCalls = useMemo(() => {
    if (!searchTerm.trim()) return calls;
    const q = searchTerm.toLowerCase();
    return calls.filter((c) => (
      c.customer_name?.toLowerCase().includes(q)
      || c.customer_phone?.toLowerCase().includes(q)
      || c.lead_type_label?.toLowerCase().includes(q)
      || c.outcome_label?.toLowerCase().includes(q)
      || c.lead_label?.toLowerCase().includes(q)
      || c.deal_label?.toLowerCase().includes(q)
      || c.vehicle_info?.toLowerCase().includes(q)
    ));
  }, [calls, searchTerm]);

  const openCallDetail = async (callId) => {
    setModalOpen(true);
    setDetailLoading(true);
    setDetailCall(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/outreach/calls/${callId}`, { headers });
      setDetailCall(res.data.call);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load call details');
      setModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setDetailCall(null);
  };

  const handlePlayRecording = (e, call) => {
    e.stopPropagation();
    const url = call.primary_recording_url || call.recording_url;
    if (url) {
      openCallDetail(call.id);
    } else {
      toast.error('No recording available for this call');
    }
  };

  const handleDownload = (e, url, label) => {
    e.stopPropagation();
    if (!url) {
      toast.error(`No ${label} available`);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 max-w-[1500px] mx-auto"} data-testid="outreach-calls-panel">
      {embedded ? null : (
      <div className="mb-6">
        <h1 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-2">
          <PhoneCall className="w-6 h-6 text-blue-400" />
          Outreach Agent
        </h1>
        <p className="text-sm font-['Manrope'] text-white/40 mt-1">
          Dograh AI voice calls — recordings, transcripts, and linked leads
        </p>
      </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/5 border border-white/10 p-5">
          <p className="text-white/50 text-sm font-['Manrope'] mb-1">Total Calls</p>
          <p className="text-3xl font-['Unbounded'] font-bold text-white">{total}</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-5">
          <p className="text-white/50 text-sm font-['Manrope'] mb-1">With Recording</p>
          <p className="text-3xl font-['Unbounded'] font-bold text-blue-400">
            {calls.filter((c) => c.has_recording).length}
          </p>
        </div>
        <div className="bg-white/5 border border-white/10 p-5">
          <p className="text-white/50 text-sm font-['Manrope'] mb-1">With Transcript</p>
          <p className="text-3xl font-['Unbounded'] font-bold text-emerald-400">
            {calls.filter((c) => c.has_transcript).length}
          </p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search by customer, phone, lead type, outcome, deal, or vehicle…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-2.5 font-['Manrope'] text-sm focus:outline-none focus:border-blue-500/50"
          />
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/10 overflow-hidden">
        {loading ? (
          <p className="text-white/40 font-['Manrope'] text-sm p-12 text-center">Loading outreach calls…</p>
        ) : filteredCalls.length === 0 ? (
          <div className="p-12 text-center">
            <PhoneCall className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 font-['Manrope'] text-sm">No outreach calls found</p>
            <p className="text-white/30 font-['Manrope'] text-xs mt-1">
              Calls completed by Dograh will appear here automatically.
            </p>
          </div>
        ) : (
          <>
          <div className="lg:hidden space-y-3 p-3">
            {filteredCalls.map((call) => (
              <AdminRecordCard
                key={call.id}
                title={call.customer_name}
                subtitle={call.customer_phone}
                badge={<OutcomeBadge outcome={call.outcome} label={call.outcome_label} />}
                fields={[
                  { label: 'When', value: formatDate(call.created_at) },
                  { label: 'Type', value: call.lead_type_label || '—' },
                  { label: 'Duration', value: call.duration_label || '—' },
                ]}
                onClick={() => openCallDetail(call.id)}
                primary={
                  <button
                    type="button"
                    onClick={() => openCallDetail(call.id)}
                    className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                  >
                    Open
                  </button>
                }
              />
            ))}
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  {[
                    'Date / Time',
                    'Customer',
                    'Lead Type',
                    'Outcome',
                    'Duration',
                    'Linked Lead / Deal / Vehicle',
                    'Actions',
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-['Manrope'] font-semibold text-white/70 uppercase tracking-wider"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredCalls.map((call) => (
                  <tr
                    key={call.id}
                    onClick={() => openCallDetail(call.id)}
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm font-['Manrope'] text-white/80 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-white/40" />
                        {formatDate(call.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-['Manrope'] text-white font-medium">{call.customer_name}</p>
                      {call.customer_phone && (
                        <p className="text-xs font-['Manrope'] text-white/40">{call.customer_phone}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-['Manrope'] text-white/60">
                      {call.lead_type_label}
                    </td>
                    <td className="px-4 py-3">
                      <OutcomeBadge outcome={call.outcome} label={call.outcome_label} />
                    </td>
                    <td className="px-4 py-3 text-sm font-['Manrope'] text-white/60">
                      {call.duration_label}
                    </td>
                    <td className="px-4 py-3 text-xs font-['Manrope'] text-white/50 space-y-0.5">
                      {call.lead_label && (
                        <p>
                          Lead:{' '}
                          {call.lead_type === 'service_request' ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/admin/service-requests/${call.lead_id}`); }}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              {call.lead_label}
                            </button>
                          ) : (
                            <span className="text-white/60">{call.lead_label}</span>
                          )}
                        </p>
                      )}
                      {call.deal_id && (
                        <p>
                          Deal:{' '}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/deals/${call.deal_id}`); }}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {call.deal_label || call.deal_id}
                          </button>
                        </p>
                      )}
                      {call.vehicle_info && call.vehicle_info !== '—' && (
                        <p className="text-white/40">{call.vehicle_info}</p>
                      )}
                      {!call.lead_label && !call.deal_id && call.vehicle_info === '—' && (
                        <span>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          title="View details & transcript"
                          onClick={() => openCallDetail(call.id)}
                          className="p-2 hover:bg-white/10 text-blue-400"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="Play recording"
                          onClick={(e) => handlePlayRecording(e, call)}
                          disabled={!call.has_recording}
                          className="p-2 hover:bg-white/10 text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="Download recording"
                          onClick={(e) => handleDownload(e, call.primary_recording_url, 'recording')}
                          disabled={!call.has_recording}
                          className="p-2 hover:bg-white/10 text-white/60 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="View transcript"
                          onClick={() => openCallDetail(call.id)}
                          disabled={!call.has_transcript}
                          className="p-2 hover:bg-white/10 text-amber-400 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <OutreachCallDetailModal
        open={modalOpen}
        call={detailCall}
        loading={detailLoading}
        onClose={closeModal}
        onNavigate={(path) => { closeModal(); navigate(path); }}
      />
    </div>
  );
}